/**
 * The fields of one transition (spec 040 D-4): type, direction (slide and
 * wipe only), duration in ms and easing. The Transitions tab's Enter and
 * Exit blocks and the timeline's background-change popover all edit a
 * `Transition` through it; `onChange` gets the fields changed.
 */
import React from 'react';
import type { Transition, TransitionDirection, TransitionType } from '../../../../../Shared/src/@types';

export const TRANSITION_TYPES: { value: TransitionType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide', label: 'Slide' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'wipe', label: 'Wipe' },
];

export const DIRECTION_OPTIONS: { value: TransitionDirection; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
];

export const EASING_OPTIONS = [
  { value: 'power1.out', label: 'Ease Out' },
  { value: 'power1.in', label: 'Ease In' },
  { value: 'power1.inOut', label: 'Ease In-Out' },
  { value: 'power2.out', label: 'Ease Out (Strong)' },
  { value: 'power2.inOut', label: 'Ease In-Out (Strong)' },
  { value: 'elastic.out', label: 'Elastic' },
  { value: 'bounce.out', label: 'Bounce' },
  { value: 'none', label: 'Linear' },
];

export interface TransitionFieldsProps {
  value: Transition;
  onChange: (updates: Partial<Transition>) => void;
  /** The wrapper of each label + control (the panel's field class, or the popover's row class). */
  fieldClassName?: string;
  /** Prefixes the controls' accessible names ("Background change type"). */
  label?: string;
}

const TransitionFields: React.FC<TransitionFieldsProps> = ({ value, onChange, fieldClassName, label }) => {
  const named = (what: string) => (label ? `${label} ${what.toLowerCase()}` : undefined);
  return (
    <>
      <div className={fieldClassName}>
        <label>Type</label>
        <select
          value={value.type}
          aria-label={named('Type')}
          data-testid="transition-type"
          onChange={(e) => onChange({ type: e.target.value as TransitionType })}
        >
          {TRANSITION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {value.type !== 'none' && (
        <>
          {(value.type === 'slide' || value.type === 'wipe') && (
            <div className={fieldClassName}>
              <label>Direction</label>
              <select
                value={value.direction || 'left'}
                aria-label={named('Direction')}
                data-testid="transition-direction"
                onChange={(e) => onChange({ direction: e.target.value as TransitionDirection })}
              >
                {DIRECTION_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className={fieldClassName}>
            <label>Duration (ms)</label>
            <input
              type="number"
              value={value.duration}
              aria-label={named('Duration (ms)')}
              data-testid="transition-duration"
              onChange={(e) => onChange({ duration: parseInt(e.target.value) || 500 })}
              min={0}
              max={5000}
              step={100}
            />
          </div>

          <div className={fieldClassName}>
            <label>Easing</label>
            <select
              value={value.ease || 'power1.out'}
              aria-label={named('Easing')}
              data-testid="transition-easing"
              onChange={(e) => onChange({ ease: e.target.value })}
            >
              {EASING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </>
  );
};

export default TransitionFields;
